import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, deleteFn } from '@/lib/api';
import { PopulationRow } from '../types/insights';

export function usePopulation(selectedFunction: string) {
  const queryClient = useQueryClient();

  // Fetch all population rows for the given function
  const {
    data = [],
    isLoading,
    error,
  } = useQuery<PopulationRow[], Error>({
    queryKey: ['population', selectedFunction],
    queryFn: async () => {
      const res = await get<{ data: PopulationRow[] }>('/insights/population', { function: selectedFunction });
      return res.data;
    },
    enabled: !!selectedFunction,
  });

  // Add or update a population row
  const upsertMutation = useMutation({
    mutationFn: async (row: PopulationRow) => {
      await post('/insights/population', row);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['population', selectedFunction] });
    },
  });

  // Delete a population row
  const deleteMutation = useMutation({
    mutationFn: async (row: PopulationRow) => {
      await deleteFn('/insights/population?year=' + encodeURIComponent(row.year) + '&function=' + encodeURIComponent(row.function));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['population', selectedFunction] });
    },
  });

  return {
    data,
    isLoading,
    error,
    addOrUpdatePopulation: upsertMutation.mutate,
    addOrUpdatePopulationAsync: upsertMutation.mutateAsync,
    deletePopulation: deleteMutation.mutate,
    deletePopulationAsync: deleteMutation.mutateAsync,
    isSaving: upsertMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
} 